import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';

function getUser() {
  try { return JSON.parse(localStorage.getItem('knowfi_user') || 'null'); } catch { return null; }
}

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function Pricing() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const user = getUser();
  const isPro = user?.plan?.toLowerCase() === 'pro';

  const handleUpgrade = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/subscribe`, { method: 'POST' });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Something went wrong. Try again.');
        setLoading(false);
      }
    } catch (err) {
      setError('Could not connect to payment server. Try again.');
      setLoading(false);
    }
  };

  return (
    <>
    <div style={styles.page}>
      <SEO
        title="KnowFi Pro — $12/mo | Hedge Funds, AI, Courses & More Unlocked"
        description="Upgrade to KnowFi Pro for the Smart Money hedge fund tracker, weekly & monthly market digest, 278 guides, 9 calculators, Pro Market Intel, and your complete Money Game Plan. $12/month."
        path="/pricing"
      />
      <Helmet>
        <meta name="keywords" content="personal finance app, financial literacy app, KnowFi Pro, hedge fund tracker, financial education subscription, money management app" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'KnowFi Pro',
          applicationCategory: 'FinanceApplication',
          operatingSystem: 'Web',
          description: 'Personal finance education platform with AI financial coach, hedge fund portfolio tracker, market data, courses, and personalized money game plans.',
          url: 'https://knowfi.io/pricing',
          offers: {
            '@type': 'Offer',
            price: '12.00',
            priceCurrency: 'USD',
            priceValidUntil: '2027-01-01',
          },
          aggregateRating: { '@type': 'AggregateRating', ratingValue: '5', reviewCount: '1' },
        })}</script>
      </Helmet>
      <div style={styles.glow} />
      <div style={styles.header}>
        <h1 style={styles.title}>Simple Pricing</h1>
        <p style={styles.subtitle}>Most people pay thousands for a financial advisor. KnowFi gives you the tools to figure it out yourself — for $12 a month.</p>
      </div>

      <div style={styles.plansRow}>
        {/* Free */}
        <div style={styles.planCard}>
          <h2 style={styles.planName}>Free</h2>
          <div style={styles.priceRow}><span style={styles.price}>$0</span><span style={styles.period}>/month</span></div>
          <p style={styles.planDesc}>A solid foundation. Start learning right now.</p>
          <Link to="/signup" style={styles.btnFree}>Get Started Free</Link>
        </div>

        {/* Pro */}
        <div style={styles.planPro}>
          <div style={styles.badge}>Most Popular</div>
          <h2 style={styles.planName}>KnowFi Pro</h2>
          <div style={styles.priceRow}>
            <span style={{ ...styles.price, color: '#38bdf8' }}>$12</span>
            <span style={styles.period}>/month</span>
          </div>
          <p style={styles.planDesc}>The financial intelligence most people never had access to — now in your pocket for less than a Netflix subscription.</p>
          {error && <p style={{ color: '#f87171', fontSize: '0.85rem', marginBottom: '0.5rem' }}>{error}</p>}
          <button
            onClick={handleUpgrade}
            disabled={loading}
            style={{ ...styles.btnPro, opacity: loading ? 0.7 : 1, cursor: loading ? 'wait' : 'pointer', border: 'none', width: '100%' }}
          >
            {loading ? 'Redirecting to checkout...' : 'Get KnowFi Pro — $12/mo →'}
          </button>
        </div>
      </div>

      {/* FAQ */}
      <div style={styles.faq}>
        <h3 style={styles.faqTitle}>Common Questions</h3>
        <div style={styles.faqGrid}>
          {faq.map((item, i) => (
            <div key={i} style={styles.faqCard}>
              <h4 style={styles.faqQ}>{item.q}</h4>
              <p style={styles.faqA}>{item.a}</p>
            </div>
          ))}
        </div>
      </div>

      <p style={styles.legal}>⚠️ KnowFi provides financial education only. Not investment advice. Always consult a qualified financial advisor for personal decisions.</p>
    </div>

    {/* Sticky upgrade bar — free logged-in users only */}
    {user && !isPro && (
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 999,
        background: 'linear-gradient(90deg, #0d1b35 0%, #1a0f35 100%)',
        borderTop: '1px solid rgba(167,139,250,0.35)',
        padding: '14px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12,
        boxShadow: '0 -4px 30px rgba(167,139,250,0.12)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 22 }}>⭐</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, color: '#f1f5f9' }}>Upgrade to KnowFi Pro</div>
            <div style={{ fontSize: 13, color: '#94a3b8' }}>Unlock Smart Money, full AI, all courses + more — $12/mo</div>
          </div>
        </div>
        <button
          onClick={handleUpgrade}
          disabled={loading}
          style={{
            background: 'linear-gradient(135deg, #38bdf8, #a78bfa)',
            color: '#fff', border: 'none', borderRadius: 10,
            padding: '12px 28px', fontWeight: 800, fontSize: 15,
            cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.7 : 1,
            boxShadow: '0 0 20px rgba(56,189,248,0.25)',
            flexShrink: 0,
          }}>
          {loading ? 'Redirecting…' : 'Get Pro Now — $12/mo'}
        </button>
      </div>
    )}
  </>
  );
}

const freeFeatures = [
  'AI financial coach — ask anything, get plain-English answers',
  'Jargon & headline translator',
  'All 9 financial calculators (retirement, debt, compound interest & more)',
  'All 6 finance courses — 278 guides across investing, crypto, budgeting & more',
  'Daily KnowFi Brief — what moved in markets today, explained simply',
  'Financial Health Score',
  'Money Game Plan (first 2 steps)',
  'Live market prices — BTC, ETH, S&P 500, Gold & more',
];

const lockedFeatures = [
  'Smart Money Tracker — see what hedge funds & insiders are actually buying',
  'Weekly Digest + Monthly Outlook — the big picture with action items',
  'Pro Market Intel (VIX, Fear & Greed, Sector Performance + more)',
  'Complete Money Game Plan with personalized investing projections',
];

const proFeatures = [
  'Everything in Free — all 278 guides, 9 calculators, 6 courses, daily brief, health score',
  '🧠 Smart Money Tracker — see exactly what Warren Buffett, Citadel, Renaissance & 6 other top funds are holding, plus real dollar amounts on insider trades',
  '📬 Weekly Digest & Monthly Outlook — the big picture in markets every week and month, with specific action items you can actually act on',
  '📊 Pro Market Intel — VIX fear gauge, Fear & Greed Index, BTC Dominance, Sector Performance, extended crypto (XRP/SOL/AVAX) & global currencies',
  '🎯 Complete Money Game Plan — personalized step-by-step plan with investing projections based on your income, debt & goals',
  '⚡ New tools & guides added regularly — you get everything as it ships',
];

const faq = [
  { q: 'Is $12/month actually worth it?', a: 'The average American pays $300+ per hour for a financial advisor — and most never get one. KnowFi Pro gives you hedge fund tracking, AI coaching, personalized game plans, and daily market intelligence for less than a dinner out. If it helps you make one smarter financial decision, it\'s paid for itself.' },
  { q: 'Can I cancel anytime?', a: 'Yes. Cancel at any time from your account settings. No contracts, no cancellation fees, no drama. Your access stays active through the end of your billing period.' },
  { q: 'What is the Smart Money Tracker?', a: 'Every quarter, hedge funds managing $100M+ must file their holdings with the SEC. The Smart Money Tracker pulls that data automatically — so you can see exactly what Berkshire Hathaway, Citadel, Renaissance Technologies, and 6 other top funds own. You also get dollar amounts on recent insider trades (executives buying their own company\'s stock).' },
  { q: 'Is this real financial advice?', a: 'No — KnowFi is financial education. We explain how things work in plain English. Think of it as the smart, financially-savvy friend you never had. We\'re not licensed advisors, and nothing here is a personalized recommendation.' },
  { q: 'Is the market data real-time?', a: 'Data is sourced from public APIs and may be slightly delayed. It\'s for learning and context — not for day trading decisions.' },
  { q: 'What AI powers the coach?', a: 'KnowFi uses Groq-powered Llama — fast, private, and available 24/7. It\'s the same family of models used by major AI companies, tuned for clear financial explanations.' },
];

const styles = {
  page: { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '60px 40px', maxWidth: '900px', margin: '0 auto', position: 'relative' },
  glow: { position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '700px', height: '500px', background: 'radial-gradient(ellipse at center, rgba(56,189,248,0.08) 0%, transparent 60%)', pointerEvents: 'none', zIndex: 0 },
  header: { textAlign: 'center', position: 'relative', zIndex: 1, marginBottom: '48px' },
  title: { fontSize: '48px', fontWeight: '800', marginBottom: '12px', letterSpacing: '-1px' },
  subtitle: { color: '#64748b', fontSize: '18px', margin: 0 },
  plansRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', position: 'relative', zIndex: 1, marginBottom: '56px', alignItems: 'start' },
  planCard: { background: '#1e293b', borderRadius: '20px', padding: '36px', border: '1px solid #334155' },
  planPro: { background: 'linear-gradient(180deg, rgba(56,189,248,0.05) 0%, #1e293b 60%)', borderRadius: '20px', padding: '36px', border: '2px solid #38bdf8', position: 'relative' },
  badge: { background: '#38bdf8', color: '#0f172a', fontSize: '11px', fontWeight: '800', padding: '4px 12px', borderRadius: '20px', display: 'inline-block', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  planName: { fontSize: '24px', fontWeight: '800', margin: '0 0 8px' },
  priceRow: { display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '10px' },
  price: { fontSize: '56px', fontWeight: '800', lineHeight: 1 },
  period: { fontSize: '18px', color: '#64748b' },
  planDesc: { color: '#64748b', fontSize: '14px', margin: '0 0 24px' },
  list: { listStyle: 'none', padding: 0, margin: '0 0 28px' },
  item: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 0', borderBottom: '1px solid #334155', fontSize: '14px', color: '#e2e8f0' },
  btnFree: { display: 'block', textAlign: 'center', background: 'transparent', color: '#64748b', border: '1px solid #334155', padding: '13px', borderRadius: '10px', fontWeight: '600', fontSize: '15px', textDecoration: 'none' },
  btnPro: { display: 'block', textAlign: 'center', background: '#38bdf8', color: '#0f172a', padding: '14px', borderRadius: '10px', fontWeight: '700', fontSize: '16px', textDecoration: 'none' },
  faq: { position: 'relative', zIndex: 1, marginBottom: '40px' },
  faqTitle: { fontSize: '22px', fontWeight: '700', marginBottom: '20px' },
  faqGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '14px' },
  faqCard: { background: '#1e293b', borderRadius: '12px', padding: '20px', border: '1px solid #334155' },
  faqQ: { fontSize: '15px', fontWeight: '700', margin: '0 0 8px', color: '#f1f5f9' },
  faqA: { fontSize: '14px', color: '#64748b', lineHeight: 1.6, margin: 0 },
  legal: { color: '#334155', fontSize: '12px', textAlign: 'center', lineHeight: 1.6, position: 'relative', zIndex: 1 },
};

export default Pricing;
